"use client";

import { c as _c } from "react/compiler-runtime";
import React, { type JSX, useMemo } from "react";
import { useSetWaveData } from "@/contexts/TitleContext";
import { useContentTab } from "../ContentTabContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MyStreamWaveChat from "./MyStreamWaveChat";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./MyStreamWaveLeaderboard";
import MyStreamWaveOutcome from "./MyStreamWaveOutcome";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import { MyStreamWaveTab } from "@/types/waves.types";
import { MyStreamWaveTabs } from "./tabs/MyStreamWaveTabs";
import MyStreamWaveMyVotes from "./votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./MyStreamWaveFAQ";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { createBreakpoint } from "react-use";
import { getHomeFeedRoute } from "@/helpers/navigation.helpers";
interface MyStreamWaveProps {
  readonly waveId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
const MyStreamWave: React.FC<MyStreamWaveProps> = t0 => {
  const $ = _c(55);
  const {
    waveId
  } = t0;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const {
    waves,
    directMessages
  } = useMyStream();
  let t1;
  if ($[0] !== pathname || $[1] !== router || $[2] !== searchParams) {
    t1 = () => {
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.delete("wave");
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeFeedRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[0] = pathname;
    $[1] = router;
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1 || $[5] !== waveId) {
    t2 = {
      waveId,
      onWaveNotFound: t1
    };
    $[4] = t1;
    $[5] = waveId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    data: wave
  } = useWaveData(t2);
  let t3;
  if ($[7] !== directMessages.list || $[8] !== waveId || $[9] !== waves.list) {
    t3 = waves.list.find(w_0 => w_0.id === waveId) ?? directMessages.list.find(w => w.id === waveId);
    $[7] = directMessages.list;
    $[8] = waveId;
    $[9] = waves.list;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const waveFromList = t3;
  const t4 = waveFromList?.newDropsCount.count ?? 0;
  const t5 = waveFromList?.firstUnreadDropSerialNo ?? null;
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = {
      newDropsCount: t4,
      firstUnreadSerialNo: t5
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const enhancedData = t6;
  const newDropsCount = enhancedData.newDropsCount;
  let t7;
  if ($[14] !== newDropsCount || $[15] !== wave) {
    t7 = wave ? {
      name: wave.name,
      newItemsCount: newDropsCount
    } : null;
    $[14] = newDropsCount;
    $[15] = wave;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  useSetWaveData(t7);
  const stableWaveKey = `wave-${waveId}`;
  const {
    activeContentTab
  } = useContentTab();
  useBreakpoint();
  let t8;
  if ($[17] !== pathname || $[18] !== router || $[19] !== searchParams) {
    t8 = drop => {
      const params_0 = new URLSearchParams(searchParams?.toString() || "");
      params_0.set("drop", drop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[17] = pathname;
    $[18] = router;
    $[19] = searchParams;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const onDropClick = t8;
  if (!wave) {
    return null;
  }
  let t9;
  if ($[21] !== enhancedData.firstUnreadSerialNo || $[22] !== wave) {
    t9 = <MyStreamWaveChat wave={wave} firstUnreadSerialNo={enhancedData.firstUnreadSerialNo} />;
    $[21] = enhancedData.firstUnreadSerialNo;
    $[22] = wave;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== onDropClick || $[25] !== wave) {
    t10 = <MyStreamWaveLeaderboard wave={wave} onDropClick={onDropClick} />;
    $[24] = onDropClick;
    $[25] = wave;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== onDropClick || $[28] !== wave) {
    t11 = <WaveWinners wave={wave} onDropClick={onDropClick} />;
    $[27] = onDropClick;
    $[28] = wave;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== wave) {
    t12 = <MyStreamWaveOutcome wave={wave} />;
    $[30] = wave;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== onDropClick || $[33] !== wave) {
    t13 = <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} />;
    $[32] = onDropClick;
    $[33] = wave;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== wave) {
    t14 = <MyStreamWaveFAQ wave={wave} />;
    $[35] = wave;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t13 || $[41] !== t14 || $[42] !== t9) {
    t15 = {
      [MyStreamWaveTab.CHAT]: t9,
      [MyStreamWaveTab.LEADERBOARD]: t10,
      [MyStreamWaveTab.WINNERS]: t11,
      [MyStreamWaveTab.OUTCOME]: t12,
      [MyStreamWaveTab.MY_VOTES]: t13,
      [MyStreamWaveTab.FAQ]: t14
    };
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t9;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const components = t15;
  let t16;
  if ($[44] !== wave) {
    t16 = <MyStreamWaveTabs wave={wave} />;
    $[44] = wave;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== activeContentTab) {
    t17 = getContentTabPanelId(activeContentTab);
    $[46] = activeContentTab;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const t18 = components[activeContentTab];
  let t19;
  if ($[48] !== t17 || $[49] !== t18) {
    t19 = <div className="tw-relative tw-flex-grow tw-overflow-hidden" role="tabpanel" id={t17}>{t18}</div>;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== stableWaveKey || $[52] !== t16 || $[53] !== t19) {
    t20 = <div className="tailwind-scope tw-relative tw-flex tw-h-full tw-flex-col" key={stableWaveKey}>{t16}{t19}</div>;
    $[51] = stableWaveKey;
    $[52] = t16;
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  return t20;
};
export default React.memo(MyStreamWave);